import { OrganizationDataRecipientDto } from "../../../../../common/service/server/dto/OrganizationDataRecipientDto";

// Maps a vendor's catalog category to the default processing purposes that using such a
// vendor implies, powering the purposes page's "Why we recommend … because you are using:
// <vendors>" provenance. Purpose values are default-activity NAMES (slug is nullable in
// production). Conservative by design: only pairings where the vendor category is direct
// evidence of the purpose. A purpose with no vendor match is not recommended at all — we only
// recommend what this org's own inventory justifies.
const PURPOSES_BY_VENDOR_CATEGORY: Record<string, string[]> = {
  "Ad Network": ["Delivering Targeted Ads", "Creating Customer Profiles"],
  "Payment Processor": ["Processing Payments", "Preventing Fraud"],
  "Data Analytics Provider": ["Analyzing Data", "Tracking Purchases & Customer Data"],
  "Sales & Marketing Tool": ["Sending Promotional Communications"],
  "Commerce Software Tool": ["Fulfilling Customer Orders", "Tracking Purchases & Customer Data"],
  "Shipping Service": ["Fulfilling Customer Orders"],
  "Customer Support Tool": ["Providing Customer Support"],
  "IT Infrastructure Service": [
    "Storing and Managing Data",
    "Operating Our Website or Mobile Apps",
  ],
  "Cybersecurity Provider": ["Providing Cybersecurity"],
};

// Vendor-name overrides for tools whose purpose evidence is more specific than their
// catalog category (e.g. survey tools file under Sales & Marketing / Data Analytics).
const PURPOSES_BY_VENDOR_NAME: Record<string, string[]> = {
  Typeform: ["Conducting Surveys"],
  SurveyMonkey: ["Conducting Surveys"],
};

export type PurposeVendorEvidence = { name: string; logoUrl?: string };

/**
 * For each default-purpose name, the org's vendors that make it a recommendation.
 * Order within a purpose follows the org's vendor list; vendors appear once per purpose.
 */
export function purposeVendorEvidence(
  vendors: Pick<OrganizationDataRecipientDto, "name" | "logoUrl" | "category">[],
): Record<string, PurposeVendorEvidence[]> {
  const evidence: Record<string, PurposeVendorEvidence[]> = {};
  for (const vendor of vendors) {
    const purposes = new Set([
      ...(PURPOSES_BY_VENDOR_CATEGORY[vendor.category ?? ""] ?? []),
      ...(PURPOSES_BY_VENDOR_NAME[vendor.name] ?? []),
    ]);
    for (const purpose of purposes) {
      const list = (evidence[purpose] ??= []);
      if (!list.some((v) => v.name === vendor.name)) {
        list.push({ name: vendor.name, logoUrl: vendor.logoUrl });
      }
    }
  }
  return evidence;
}
